import classNames from 'classnames';
import * as inflection from 'inflection';
import * as _ from 'lodash-es';
import React, { useState } from 'react';
import PostDetailPreview from './PostDetailPreview';
import PostIcon from './PostIcon';
import * as styles from './PostRow.module.scss';
import PostTitleWithIcons from './PostTitleWithIcons';
import Avatar from '../components/Avatar';
import AvatarStack from '../components/AvatarStack';
import Button from '../components/Button';
import HoverPreviewable from '../components/HoverPreviewable';
import OverflowMenu from '../components/OverflowMenu';
import SVGIcon from '../components/SVGIcon';
import PostSeriesMeta from './series/PostSeriesMeta';
import Timestamp from '../components/Timestamp';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { PostType } from '../graphql/generated/graphql';
import overflowMenuIcon from '../icons/non-prefilled/16x16/overflowMenu.svg';
import pinIcon from '../icons/prefilled/16x16/pinOutlineWithStroke.svg';
import { usePostSeries } from '../series/useAllSeries';
import { useAllUsersMap } from '../user/useAllUsers';
import UserDetailPreviewPopOver from '../user/UserDetailPreviewPopOver';
import NestableLink from '../utils/NestableLink';
import Link from '../utils/router/Link';
import postPath from '../utils/url/postPath';

const POST_ROW_POST_FRAGMENT = gql(`
  fragment PostRow_post on SlimPostFields {
    id
    type
    contributors {
      id
    }
    ...PostIcon_post
    ...postPath_post
    ...PostTitleWithIcons_post
  }
`);

type PostFragment = DocumentType<typeof POST_ROW_POST_FRAGMENT>;

type MetaAttributeMap = {
  publishedAt: string;
  updatedAt: string;
  userViewedAt: string;
  insertedAt: string;
  popularity: number;
};

interface MetaProps<Attribute extends keyof MetaAttributeMap> {
  attribute: Attribute;
  values: Pick<MetaAttributeMap, Attribute>;
}

const Meta = <Attribute extends keyof MetaAttributeMap>(
  props: MetaProps<Attribute>,
) => {
  const mappedValue = props.values[props.attribute];
  if (mappedValue == null) return null;

  switch (props.attribute) {
    case 'popularity': {
      if (typeof mappedValue === 'number') {
        return (
          <>
            {mappedValue} {inflection.inflect('view', mappedValue)}
          </>
        );
      }

      return null;
    }
    default:
      return <Timestamp date={mappedValue} />;
  }
};

interface PostRowProps {
  pinned?: boolean;
  post: PostFragment;
  seriesHoverCardEnabled?: boolean;
  renderOverflowMenu?: (close: () => void) => React.ReactNode;
  forcePreviewOpen?: boolean;
  meta?: React.ReactNode;
}

const PostRow = ({
  renderOverflowMenu,
  pinned = false,
  post,
  seriesHoverCardEnabled = false,
  forcePreviewOpen,
  meta,
}: PostRowProps) => {
  const path = postPath(post);
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);

  const postSeries = usePostSeries(post.id);

  const allUsers = useAllUsersMap();
  const users = _.compact(
    post.contributors
      .map((contributor) => contributor.id)
      .map((id) => allUsers[id]),
  ).slice(0, 3);

  return (
    <NestableLink
      to={path}
      className={styles.link}
      aria-label={`Post: ${post.title}`}
      role="article"
    >
      <HoverPreviewable
        renderPreview={({ isOpen }) => (
          <PostDetailPreview postId={post.id} open={isOpen} />
        )}
        forcePreviewOpen={forcePreviewOpen}
      >
        {(targetProps) => (
          <div className={styles.container}>
            <div className={styles.iconContainer}>
              {pinned ? (
                <>
                  <PostIcon post={post} />
                  <div
                    className={classNames(styles.pinContainer, {
                      [styles.post]: post.type === PostType.Post,
                      [styles.template]: post.type === PostType.Template,
                    })}
                  >
                    <SVGIcon size="small">{pinIcon}</SVGIcon>
                  </div>
                </>
              ) : (
                <PostIcon post={post} />
              )}
              {/* TODO: Fix this the next time the file is edited. */}
              {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
              <Link
                to={path}
                className={styles.hoverTarget}
                aria-hidden
                tabIndex={-1}
                {...targetProps}
              />
            </div>
            <div className={styles.titleContainer}>
              {!!postSeries && (
                <header>
                  <Link className={styles.seriesHoverTargetContainer} to={path}>
                    <div className={styles.seriesHoverTarget}>
                      <PostSeriesMeta
                        postId={post.id}
                        series={postSeries}
                        showHoverCard={seriesHoverCardEnabled}
                      />
                    </div>
                  </Link>
                </header>
              )}
              <span className={styles.title} role="heading" aria-level={5}>
                <PostTitleWithIcons post={post} />
              </span>
            </div>
            <footer className={styles.contributors}>
              <AvatarStack items={users} noWrap>
                {(user, options) => (
                  <UserDetailPreviewPopOver
                    userId={user.id}
                    placement="top-start"
                  >
                    <Avatar user={user} size={options.size} />
                  </UserDetailPreviewPopOver>
                )}
              </AvatarStack>
            </footer>
            <footer className={styles.meta}>{meta}</footer>
            {renderOverflowMenu && (
              <aside
                className={classNames(styles.overflowMenu, {
                  [styles.isOpen]: isOverflowMenuOpen,
                })}
              >
                <OverflowMenu
                  preventCloseOnContentClick
                  button={
                    <Button
                      className={styles.overflowMenuButton}
                      aria-label="Actions"
                    >
                      <SVGIcon>{overflowMenuIcon}</SVGIcon>
                    </Button>
                  }
                  onOpenChange={setIsOverflowMenuOpen}
                  open={isOverflowMenuOpen}
                >
                  <div className={styles.overflowMenuContent}>
                    {renderOverflowMenu(() => setIsOverflowMenuOpen(false))}
                  </div>
                </OverflowMenu>
              </aside>
            )}
          </div>
        )}
      </HoverPreviewable>
    </NestableLink>
  );
};

PostRow.Meta = Meta;

export default PostRow;
